import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Download, Share2, Copy, Check, Link } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NeedsList({ items }) {
  if (!items) return null;
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="text-sm text-foreground flex items-start gap-2">
          <span className="text-green-600">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function DontNeedList({ items }) {
  if (!items) return null;
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
          <span>•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function DisclosureCard() {
  const { conditionId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [condition, setCondition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareUrl, setShareUrl] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [creatingShare, setCreatingShare] = useState(false);

  useEffect(() => {
    fetch('/conditionsCards.json')
      .then(res => res.json())
      .then(data => {
        setCondition(data[conditionId] || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [conditionId]);

  const handleShare = async () => {
    if (!token) {
      toast.error('Please sign in to share cards');
      navigate('/auth');
      return;
    }

    setCreatingShare(true);
    try {
      const response = await axios.post(
        `${API}/conditions/share/${conditionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const fullShareUrl = `${window.location.origin}/share/${response.data.share_id}`;
      setShareUrl(fullShareUrl);
      setShowShareModal(true);
    } catch (err) {
      toast.error('Failed to create share link');
    } finally {
      setCreatingShare(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  }

  if (!condition) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="font-heading text-xl font-semibold">Condition Not Found</h2>
          <Button onClick={() => navigate('/conditions')} className="mt-4">Browse Conditions</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="disclosure-card-page">
      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowShareModal(false)}>
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()} data-testid="share-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-primary/10 rounded-2xl">
                <Link className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-foreground">Share This Card</h3>
                <p className="text-sm text-muted-foreground">Anyone with this link can view the card</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 bg-secondary/50 rounded-xl p-3 mt-4">
              <input 
                type="text" 
                value={shareUrl} 
                readOnly 
                className="flex-1 bg-transparent text-sm text-foreground outline-none"
                data-testid="share-url-input"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={copyToClipboard}
                className="shrink-0"
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-3">Link expires in 30 days</p>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowShareModal(false)} className="flex-1">
                Close
              </Button>
              <Button onClick={copyToClipboard} className="flex-1 btn-primary" data-testid="copy-and-close-btn">
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="print:hidden max-w-2xl mx-auto px-6 pt-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" /><span>Back</span>
        </button>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={() => window.print()} className="btn-primary" data-testid="print-btn">
            <Printer className="h-5 w-5 mr-2" />Print Card
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="save-pdf-btn">
            <Download className="h-5 w-5 mr-2" />Save as PDF
          </Button>
          <Button 
            variant="outline" 
            onClick={handleShare} 
            disabled={creatingShare}
            data-testid="share-btn"
          >
            <Share2 className="h-5 w-5 mr-2" />
            {creatingShare ? 'Creating...' : 'Share Link'}
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-6 print:p-0">
        <div className="bg-white border-2 border-primary rounded-3xl p-8 print:border print:rounded-lg print:p-6">
          <div className="text-center border-b border-border pb-6 mb-6">
            <h1 className="font-heading text-2xl font-semibold text-primary">{condition.name}</h1>
            <p className="text-muted-foreground mt-1">{condition.description}</p>
            <p className="text-xs text-muted-foreground mt-3">This card helps communicate my needs.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-secondary/30 rounded-2xl p-5">
              <h2 className="font-heading font-semibold text-green-700 mb-3">What Helps Me</h2>
              <NeedsList items={condition.needs} />
            </div>
            <div className="bg-muted/50 rounded-2xl p-5">
              <h2 className="font-heading font-semibold text-muted-foreground mb-3">What I Don't Need to Provide</h2>
              <DontNeedList items={condition.dontNeed} />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">I have an invisible disability. Accommodations help me succeed.</p>
            <p className="text-xs text-muted-foreground mt-2">Learn more: silent-disabilities.org</p>
          </div>
        </div>

        <div className="mt-6 card-soft print:hidden">
          <h3 className="font-medium text-foreground mb-2">Tips for Using This Card</h3>
          <p className="text-sm text-muted-foreground">Print on cardstock and keep in your wallet or desk. Share with HR or healthcare providers as needed.</p>
        </div>
      </div>

      <style>{`@media print { .print\\:hidden { display: none !important; } }`}</style>
    </div>
  );
}
